import { useCallback, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { Customer } from "@/client/types.gen";
import { useDebounce } from "@/hooks/use-debounce";
import { useAuth } from "@/hooks/auth";
import { useTranslation } from "react-i18next";
import { useCustomerId } from "@/components/customer-id-provider";
import { getAllCustomersOptions } from "@/client/@tanstack/react-query.gen";
import { retryFunc } from "@/api/retry";
import { Combobox } from "@/components/ui/combobox";
import { useCart } from "./shopping-cart-provider";
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
} from "./confirmation-dialog";

export function CustomerSelect() {
  const { isEmpty } = useCart();
  const { t } = useTranslation();
  const { isCustomerUser, isLoading } = useAuth();
  const { customerId, setCustomerId } = useCustomerId();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingCustomerId, setPendingCustomerId] = useState<string | null>(
    null
  );
  const debouncedSearch = useDebounce(search, 300);

  const { data: customers, isLoading: customersLoading } = useQuery({
    ...getAllCustomersOptions({
      query: debouncedSearch ? { search: debouncedSearch } : undefined,
    }),
    retry: retryFunc,
    enabled: !isCustomerUser && !isLoading,
    placeholderData: keepPreviousData,
  });

  const handleCustomerChange = useCallback(
    (customerId: string) => {
      if (isEmpty) {
        setCustomerId(customerId, true);
      } else {
        setPendingCustomerId(customerId);
        setDialogOpen(true);
      }
    },
    [isEmpty]
  );

  const handleConfirm = useCallback(() => {
    setCustomerId(pendingCustomerId, true);
    setPendingCustomerId(null);
    setDialogOpen(false);
  }, [pendingCustomerId]);

  const handleCancel = useCallback(() => {
    setPendingCustomerId(null);
    setDialogOpen(false);
  }, []);

  const dialogProps: ConfirmationDialogProps = {
    title: t("cart.confirmDialogTitle"),
    description: t("cart.confirmDialogDescription"),
    open: dialogOpen,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };

  if (isCustomerUser) return null;

  const authReady = !isLoading;
  const items = authReady
    ? (customers?.data || []).map((customer: Customer) => ({
        value: customer.id,
        label: customer.name,
      }))
    : [];

  return (
    <div className="flex gap-2 items-center w-full max-w-xl">
      {authReady && customersLoading && (
        <Loader2 className="animate-spin inline-block ml-2" />
      )}
      <Combobox
        disabled={!authReady}
        items={items}
        value={authReady ? (customerId ?? "") : ""}
        onChange={handleCustomerChange}
        placeholder={t("langguth.selectCustomerPlaceholder")}
        emptyText={t("langguth.noCustomersFound")}
        searchPlaceholder={t("langguth.searchCustomerPlaceholder")}
        searchValue={search}
        onSearchChange={setSearch}
        getLabel={(item) => item.label}
        className="min-w-64"
      />
      <ConfirmationDialog {...dialogProps} />
    </div>
  );
}
